import React, { useState } from 'react';
import '../../styles/ComprehensionUpload.scss';

function ComprehensionUpload({ API_BASE_URL, authToken, userData, onBack }) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [questions, setQuestions] = useState([{ text: '', points: 10 }]);
  const [isPublic, setIsPublic] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleAddQuestion = () => {
    setQuestions([...questions, { text: '', points: 10 }]);
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleRemoveQuestion = (index) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/comprehensions`, {
        method: 'POST',
        headers: {
          'Authorization': authToken.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title,
          text,
          questions,
          isPublic,
          teacherId: userData.id
        })
      });

      let errorData;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        errorData = await response.json();
      } else {
        const textError = await response.text();
        throw new Error(`Server returned non-JSON response: ${textError.substring(0, 100)}...`);
      }

      if (!response.ok) {
        throw new Error(errorData.message || errorData.error || 'Failed to save comprehension');
      }

      setSuccess(true);
      // Let parent list refresh by calling onBack if provided
      if (typeof onBack === 'function') {
        onBack();
      } else {
        // Reset form as a fallback
        setTitle('');
        setText('');
        setQuestions([{ text: '', points: 10 }]);
        setIsPublic(false);
      }
    } catch (err) {
      console.error('Error saving comprehension:', err);
      setError(err.message || 'An unexpected error occurred while saving the comprehension');
    } finally {
      setIsLoading(false);
    }
  };

  // Only show this component for teachers
  if (userData?.role !== 'teacher') {
    return (
      <div className="comprehension-upload error-message max-w-2xl mx-auto mt-6 p-4 rounded-md bg-red-50 text-red-700 border border-red-200">
        This feature is only available for teachers.
      </div>
    );
  }

  return (
    <div className="comprehension-upload max-w-3xl mx-auto p-6 md:p-8 bg-white rounded-xl shadow-sm border border-slate-200">
      <button
        className="back-button inline-flex items-center text-sm text-slate-600 hover:text-slate-900 hover:underline mb-4"
        onClick={onBack}
      >
        ← Back
      </button>

      <h2 className="text-2xl font-semibold tracking-tight text-slate-900 mb-2">Create New Comprehension</h2>
      <p className="text-slate-500 mb-6">Write your passage and add questions with point values. You can make it public later if needed.</p>

      {error && <div className="error-message mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {success && <div className="success-message mb-4 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">Comprehension saved successfully!</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="form-group">
          <label htmlFor="title" className="block text-sm font-medium text-slate-700 mb-1">Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter a title for this comprehension"
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="form-group">
          <label htmlFor="text" className="block text-sm font-medium text-slate-700 mb-1">Text</label>
          <textarea
            id="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            placeholder="Enter the text for students to read"
            rows={10}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="questions-section">
          <h3 className="text-lg font-medium text-slate-900 mb-3">Questions</h3>
          {questions.map((question, index) => (
            <div key={index} className="question-group rounded-lg border border-slate-200 p-4 mb-3 bg-slate-50">
              <div className="question-header flex items-center justify-between mb-2">
                <h4 className="font-medium text-slate-800">Question {index + 1}</h4>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveQuestion(index)}
                    className="remove-question text-sm text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
              <textarea
                value={question.text}
                onChange={(e) => handleQuestionChange(index, 'text', e.target.value)}
                placeholder="Enter question text"
                required
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
              />
              <div className="points-input flex items-center gap-2">
                <label className="text-sm text-slate-700">Points</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={Number.isFinite(question.points) ? question.points : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    const parsed = v === '' ? '' : Number(v);
                    handleQuestionChange(index, 'points', parsed === '' ? 1 : (Number.isFinite(parsed) ? parsed : 1));
                  }}
                  required
                  className="w-24 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddQuestion}
            className="add-question inline-flex items-center rounded-md bg-slate-800 px-3 py-2 text-sm font-medium text-white hover:bg-slate-900"
          >
            Add Question
          </button>
        </div>

        {/* Only show the "isPublic" checkbox if the user is 'cillian' */}
        {userData?.username === 'cillian' && (
          <div className="form-group checkbox">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              Make this comprehension visible to all students
            </label>
          </div>
        )}

        <button
          type="submit"
          className="submit-button inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Comprehension'}
        </button>
      </form>
    </div>
  );
}

export default ComprehensionUpload; 